import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import { adProductLabel, LabelChip, leadProduct, leadStage, NetDot, pastLabel, type WaLabel } from "../labels";
import { ownerLabel, useOwners } from "../owners";
import { api, ApiError, listLimit, NO_FILTERS, WORK_LIST_LIMIT } from "../api/client";
import type { FacetAd, LeadDetail as Lead, LeadFacets, LeadFilters, LeadRow, Me } from "../api/types";
import { useClock, useDebounced, useInterval, useLatest, useOnReturn, useOnVisible, useToday } from "../hooks";
import { keepRows, LINK_FRESH_MS, rowSig, sameRows, type KeptRows } from "../signedLinks";
import AvatarPic from "./AvatarPic";
import { can, fmtListWhen } from "../util";
import { adName, avatarStyle, initialOf, isMessenger, phoneLabel, previewOf } from "../ui";
import LeadDetail, { preloadCard } from "./LeadDetail";
import MediaIcon from "./MediaIcon";
import { adLabel, askingAgain, hot, needsOrder, sinceWord, STEPS, type StepKey, afterSaleWords } from "../customers";
import { CHAT_JUMP, takeChatJump, type ChatJump } from "../chatJump";
import { useNetChoice } from "../netChoice";
import { useKeepPlace, usePlaceBefore } from "../fresh";
import { useBackLayer } from "../backstack";
import DealerTag from "./DealerTag";
import PageSkel from "./PageSkel";
import { Btn, Empty, stillWanted, toast } from "./Kit";
import { ShellIcon } from "./ShellIcons";
import { ChatModal } from "./SaleDialog";
import { AdPic } from "./CustomerBits";
import { addChatNote } from "../chatNotes";

interface Props {
  me: Me | null;
  /** False while another tab is showing: the pulse and the minute's read pause. */
  active: boolean;
  /** How many conversations the current filter matches, for the tab's badge. */
  onCount: (c: { n: number; capped: boolean }) => void;
  /** Customers waiting for an answer on both networks (回复客人), for the phone bar. */
  onWaiting?: (n: number) => void;
  /** Every list's number, both networks added up: 今天's 要你做 and its menu number are these. */
  onCounts?: (c: Record<string, number>) => void;
  /** A conversation another page asked to open (the customer table). */
  openRequest?: { wa: string; seq: number; from?: "followup" } | null;
  /** Back to the page that opened the chat (跟进's 「‹ 回到跟进」). */
  onBackTo?: () => void;
  /** A phone's chat was closed, by its arrow or the phone's back. */
  onChatClosed?: () => void;
  /** Whether a chat is open (.wc.open): App hides the phone's bar while it is and 对话 is on screen. */
  onChatOpen?: (open: boolean) => void;
  /** The chat it opened while off screen is drawn: App lays 对话 out once more, ahead (App warmLeads). */
  onChatDrawn?: () => void;
}

/** The two networks, and both. WhatsApp first: it carries the traffic. */
const NETWORKS = [
  { key: "", label: "全部", icon: null },
  { key: "whatsapp", label: "WhatsApp", icon: <ShellIcon name="wa" size={15} /> },
  { key: "messenger", label: "Messenger", icon: <ShellIcon name="msg" size={15} /> },
] as const;

const stepTitle = (k: StepKey) => STEPS.find((s) => s.key === k)?.title ?? "";
const stepHint = (k: StepKey) => STEPS.find((s) => s.key === k)?.hint ?? "";

/**
 * The lists a colleague works down, one at a time: the customer table's 今天要做
 * steps, with its names and its rules. They used to be lists of their own (在等回复
 * without the bot's handovers, 今天要跟进 over both networks), and the same idea
 * showed two numbers on two pages (owner, 2026-09-23). 安排发货 stays on the
 * table: it is shop work, not a chat.
 *
 * 说付了 and 货到付款 were here too, and went (owner, 2026-10-11: 「说付了要来干嘛？
 * 经常判断错误 货到付款点了出现的对话也不对」). 说付了 is a word match — 「付了钱多久到」
 * and a bot button's 「已经汇款」 count — and 小爱 takes no transfers; 货到付款 listed
 * a request until somebody pressed 已开单, and 小爱 opens those orders herself. The
 * customer table's 要收的钱 still has the first; the chat's own card the second.
 */
const QUICK = [
  // First and red: 小爱 said a colleague would come, and until one does the customer waits. It was a
  // small tag on the row and a choice behind 筛选 (owner, 2026-10-11: 「这种情况要明显一点让人去看一下」).
  { key: "needs_human", label: "要真人", title: "小爱说了请同事来、还没有同事回的对话。同事一回就自动拿掉" },
  // Beside it, the other way round: chats a colleague holds, to give back to 小爱. A colleague's reply
  // or a group send switches her off, and switching her back meant opening each chat (owner,
  // 2026-10-20: 「cs回了顾客或blasting后 需要一个一个开回小爱回复」). The chat header's own word; second,
  // so a phone shows it without sliding the line.
  { key: "held", label: "人工回复", title: "24 小时内同事回过、现在小爱不回的对话。回完按「交回小爱」，小爱接着回" },
  { key: "reply", label: stepTitle("reply"), title: "客户发了消息还没人回，或小爱请同事接手" },
  { key: "close", label: stepTitle("close"), title: stepHint("close") },
  { key: "chase", label: stepTitle("chase"), title: stepHint("chase") },
  { key: "aftersale_due", label: stepTitle("aftersale"), title: stepHint("aftersale") },
] as const;
type QuickKey = (typeof QUICK)[number]["key"];

/**
 * How many chats the list draws at first, and how many more each time it is
 * scrolled near the end of those. A work list holds up to 1,000 (listLimit):
 * drawn all at once, a press kept a slow phone (CPU 4x) busy 2–5 s, and a chat
 * moving to the top moved every row under it (2026-10-22).
 */
const FIRST_ROWS = 50;
const MORE_ROWS = 50;
/** How far below the list the next chats are drawn: they are there before the thumb is. */
const AHEAD = "0px 0px 1200px 0px";

/** How often 对话 asks whether anything changed (api.leadsPulse): a press on another screen shows within this. */
const PULSE_MS = 3000;
/**
 * The whole read beside the pulse: what moves with the clock alone (追第 N 次, the day's labels) and the shop's
 * orders on the rows, which the pulse does not see. It was the only read, every 15 seconds.
 */
const FULL_MS = 60_000;
/** How often a moved pulse reads the buttons' numbers again: the server keeps them 20 seconds (countsFresh). */
const COUNTS_MS = 20_000;

/**
 * The rarer lists, behind 筛选. They combine with everything else. 已成交 went (owner, 2026-10-27: 「没用的filter
 * 为什么不删除呢？」): who bought is 客户's 订单 and 回头客, and 售后回访 is the list a buyer is answered from; 进度 and
 * 来源 went with it, to 客户's pipeline and its 来源 column, where they carry counts and are changed in place.
 */
const MORE_LISTS = [{ key: "disqualify_hint", label: "可能不合格", title: "像是批发、只要货到付款或不在马来西亚，还没判断" }] as const;

/** Past this many ads, 筛选's ad list gets a search box. */
const AD_SEARCH = 8;
/** How many of a product's ads 筛选 shows before 「还有 N 条广告」. */
const AD_FIRST = 5;

/** A product's name and colour as the rows' product label wears them (经营 says the same words); 其他 for the rest. */
const productName = (k: string) => adProductLabel(k)?.text ?? (k === "other" ? "其他" : k);
const productColor = (k: string) => {
  const l = adProductLabel(k);
  return l ? `rgb(${l.rgb})` : "var(--ink-4)";
};

/**
 * An ad as 经营's ad list names it (ReportsView's sub rows): its headline or name, and the end of its number. With
 * neither, its campaign's short name, which 经营 shows over it: 「广告 …678903」 told nobody which ad it was.
 */
const ownName = (a: Pick<FacetAd, "headline" | "ad_name">) => a.headline || a.ad_name || "";
const facetAdName = (a: Pick<FacetAd, "headline" | "ad_name" | "ad_id" | "campaign">) =>
  adName(ownName(a) || (a.campaign ? adLabel(a.campaign) : ""), a.ad_id);

/** How many rows a list asks for: one product's or one ad's chats all at once (they are drawn 50 at a time), as a work list's are. */
const rowsFor = (f: LeadFilters) => (f.product || f.ad_id ? WORK_LIST_LIMIT : listLimit(f));

/** The one thing a row needs, most urgent first, in the customer table's words. */
function rowJob(l: LeadRow): { label: string; tone: "red" | "amber" | "green"; title: string } | null {
  if (l.needs_human) return { label: "要真人", tone: "red", title: "小爱请同事接手" };
  if (l.unanswered) return { label: "回复", tone: "red", title: "客户在等回复" };
  if (l.cod_open_at) return { label: "货到付款开单", tone: "amber", title: "客户确认了货到付款，等同事在 cp 开单" };
  if (l.paid_hint)
    return { label: l.last_order?.payment_status === "pending" ? "确认收钱" : "开单", tone: "amber", title: "客户说付了钱" };
  if (needsOrder(l)) return { label: "补开单", tone: "amber", title: "标了成交，商城没有这张单" };
  if ((l.chase_due ?? 0) > 0)
    return { label: `追第 ${l.chase_due} 次`, tone: hot(l) ? "red" : "amber", title: `客户 ${l.quiet_days ?? ""} 天没回` };
  if (l.aftersale_due) {
    const a = afterSaleWords(l);
    return { label: a.tag, tone: a.tone, title: `${a.when} · ${a.ask}` };
  }
  return null;
}

/** A row's job in the kit's Chip tones (Kit.tsx Chip). */
const JOB_CHIP = { red: "urgent", amber: "today", green: "after" } as const;

/** 筛选's 谁在回, as the header's switch says it: 小爱's ✦ and a colleague's headset. */
const WHO = [
  { value: "bot", icon: "sparkles", label: "小爱在回", title: "小爱自动回的对话" },
  { value: "human", icon: "headset", label: "同事在回", title: "同事接手了，小爱不回" },
] as const;

/** 客户详情's column, folded ("0") or not: the colleague's own choice, kept on this device. */
const PANE_KEY = "lazior_chat_pane";
/** Room beside the list (chat.css --wc-lw) for the chat at 640 and the 320 column. */
function roomForPane(width: number): boolean {
  const vw = window.innerWidth;
  return vw > 800 && width - (vw >= 1500 ? 360 : vw >= 1100 ? 340 : 320) >= 960;
}

/** Every chat (api/client NO_FILTERS, shared with 今天's work lists). */
const EMPTY: LeadFilters = NO_FILTERS;

/**
 * The chat list and the conversation, laid out as WhatsApp Web: a 30% left
 * panel of 72px rows, the chat filling the rest, one pane at a time on a phone.
 */
function LeadsView({ me, active, onCount, onWaiting, onCounts, openRequest, onBackTo, onChatClosed, onChatOpen, onChatDrawn }: Props) {
  const [filters, setFilters] = useState<LeadFilters>(EMPTY);
  // The channel is one choice with the customer table's (netChoice.ts): picking
  // WhatsApp there shows WhatsApp here.
  const [netChoice, setNetChoice] = useNetChoice();
  useEffect(() => {
    setFilters((f) => (f.network === netChoice ? f : { ...f, network: netChoice }));
  }, [netChoice]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search, 300);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  // Whether the list has answered once. Before, the console opened on
  // 「还没有对话」 until it had: a sentence about an empty list, read as one.
  const [listRead, setListRead] = useState(false);
  // Each row's Day label turns over at midnight.
  const today = useToday();
  // 负责的同事 of each customer, picked on sales.lazior.com (owners.tsx).
  const { owners } = useOwners();
  const [listError, setListError] = useState("");
  // How many the list holds, for 筛选's 看 N 个对话: the tab's number, read with the rows.
  const [listCount, setListCount] = useState<{ n: number; capped: boolean; of: string } | null>(null);
  // A reload for a new build (fresh.tsx) must not close the chat somebody had
  // open: it comes back, and on a phone they are back inside it.
  const chatBefore = usePlaceBefore("chat");
  const paneBefore = usePlaceBefore("chatPane");
  const [selected, setSelected] = useState<string | null>(chatBefore ?? null);
  useKeepPlace("chat", selected);
  // Every label seen in the list, for the filter chips. A label the filter is
  // holding stays on the row even when nothing on this page carries it, or
  // turning it off would need a reload.
  const [labelsInUse, setLabelsInUse] = useState<string[]>([]);
  /** 交回小爱 for the whole list on screen, as it goes; null when idle. */
  const [bulk, setBulk] = useState<null | { done: number; total: number }>(null);
  const [askBulk, setAskBulk] = useState(false);
  /** Each row's 交回小爱: on its way, done (the row folds away), or refused. */
  const [handed, setHanded] = useState<Record<string, "going" | "done" | "failed">>({});
  /** Rows folded out of the list after 交回小爱, until it is read without them. */
  const [folded, setFolded] = useState<ReadonlySet<string>>(() => new Set());
  // When each was done: a tick is only kept until the list has caught up.
  const doneAt = useRef<Record<string, number>>({});
  /** What was just handed back, for 撤回; null when there is nothing to take back. */
  const [undo, setUndo] = useState<null | { text: string; was: LeadRow[]; held: boolean }>(null);
  const [detail, setDetail] = useState<Lead | null>(null);
  const [detailError, setDetailError] = useState("");
  const [detailOpen, setDetailOpen] = useState(paneBefore === "1"); // mobile: which pane shows
  useKeepPlace("chatPane", detailOpen ? "1" : null);
  // On a phone the open chat is a step of history: the phone's back closes it,
  // as WhatsApp's does. Without one, back inside a chat left the console.
  const closeChat = useCallback(() => {
    setDetailOpen(false);
    onChatClosed?.();
  }, [onChatClosed]);
  useBackLayer(detailOpen, closeChat);
  // Before the paint, so the phone's bar goes in the same frame the chat comes.
  useLayoutEffect(() => {
    onChatOpen?.(detailOpen);
    return () => onChatOpen?.(false);
  }, [detailOpen, onChatOpen]);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState("");
  const paneRef = useRef<HTMLDivElement>(null);
  const listSeq = useLatest();
  const detailSeq = useLatest();

  // The in-flight detail request must not overwrite the pane if the operator
  // has already clicked a different lead. Written in an effect, not during
  // render, so a discarded render can never leave a stale value behind.
  const selectedRef = useRef<string | null>(null);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    setFilters((f) => (f.q === debouncedSearch.trim() ? f : { ...f, q: debouncedSearch.trim() }));
  }, [debouncedSearch]);

  const lastRows = useRef<KeptRows>(new Map());
  // Whether the rows on screen are the 人工回复 list: its button then counts
  // them, as they are now, rather than the counts read up to a minute ago — a
  // chat handed back leaves both at once.
  const [heldShown, setHeldShown] = useState(false);
  // How many rows are drawn: from FIRST_ROWS again for each new list, while a
  // read of the same list keeps what was scrolled to.
  const [drawn, setDrawn] = useState(FIRST_ROWS);
  const drawnFor = useRef("");
  // Set by 「这条广告的对话」 (chatJump): the next list opens its first chat.
  const pickFirst = useRef(false);
  // The pulse's tags the list and the open chat on screen were read under (PULSE_MS): one the pulse no longer
  // says is read again. None yet, or from an older API: the next pulse reads it.
  const listPulse = useRef<string | undefined>(undefined);
  const chatPulse = useRef<{ wa: string; tag: string } | null>(null);
  // Until when the pulse leaves the list alone: 交回小爱's rows show their tick and fold away before it is read again.
  const listHeld = useRef(0);
  // When the list was last read, for the pulse's whole read every FULL_MS.
  const listReadAt = useRef(0);
  const loadLeads = useCallback(async () => {
    const ticket = listSeq.next();
    try {
      const data = await api.leads(filters, rowsFor(filters));
      if (!listSeq.isCurrent(ticket)) return; // a newer request already answered
      listPulse.current = data.pulse || undefined;
      listReadAt.current = Date.now();
      const rows = data.leads ?? [];
      setHeldShown(!!filters.held);
      // A handed-back row's tick lasts until the list no longer holds it. If a
      // colleague answers it again it comes back as a row like any other, not
      // folded away by a tick from before.
      setHanded((h) => {
        let n: typeof h | null = null;
        for (const [wa, st] of Object.entries(h)) {
          if (st !== "done") continue;
          const stale = rows.some((r) => r.wa_id === wa && !r.bot_enabled) && Date.now() - (doneAt.current[wa] ?? 0) < 20_000;
          if (!stale) {
            n ??= { ...h };
            delete n[wa];
          }
        }
        return n ?? h;
      });
      // A folded row stays folded only as long as its tick would: one a colleague takes again comes back.
      setFolded((f) => {
        if (!f.size) return f;
        const n = new Set([...f].filter((wa) => rows.some((r) => r.wa_id === wa && !r.bot_enabled) && Date.now() - (doneAt.current[wa] ?? 0) < 20_000));
        return n.size === f.size ? f : n;
      });
      // A chat the server sends again unchanged keeps the object on screen,
      // and its row (ChatRow, memo'd) is not drawn again. Compared whole, every
      // chat with a picture looked changed on every poll — the API signs each
      // picture's link afresh — and the whole list was redrawn and every
      // picture fetched again every 15 seconds (2026-10-20). A row drawn later
      // (FIRST_ROWS) takes the newest link: the first one may have run out.
      const { rows: next, kept } = keepRows(lastRows.current, rows, true);
      lastRows.current = kept;
      setLeads((ls) => (sameRows(ls, next) ? ls : next));
      const listKey = JSON.stringify(filters);
      if (drawnFor.current !== listKey) {
        drawnFor.current = listKey;
        setDrawn(FIRST_ROWS);
      }
      setLabelsInUse((seen) => {
        const more = new Set<string>(seen);
        for (const l of rows) for (const lb of l.labels ?? []) more.add(lb);
        return more.size === seen.length ? seen : [...more].sort();
      });
      // The backend caps a page; say so rather than under-reporting the total.
      const counted = { n: data.count ?? 0, capped: rows.length >= rowsFor(filters), of: listKey };
      onCount(counted);
      setListCount(counted);
      setListError("");
      setListRead(true);
      // 「这条广告的对话」 from another page: the list's first chat beside it, not the chat open before.
      if (pickFirst.current) {
        pickFirst.current = false;
        if (rows[0] && !window.matchMedia("(max-width: 800px)").matches) setSelected(rows[0].wa_id);
      }
    } catch (e) {
      if (!listSeq.isCurrent(ticket)) return;
      setListRead(true);
      // Keep whatever is on screen — a failed poll must not blank a list the
      // operator is working from; the banner says it is stale.
      if (!(e instanceof ApiError && e.status === 401)) setListError("列表刷新失败，显示的是旧的。");
    }
  }, [filters, onCount, listSeq]);

  // Chats opened or pointed at lately, as last read, by the number asked for:
  // going back to one shows it at once while the newest copy is read behind
  // it (owner 10/16: 「对话在切换不同用户的时候也是卡卡的」 — every switch waited
  // for the server with the previous customer still on screen).
  // at: when it was last read; since: when this copy was, which its signed
  // links are as old as.
  const opened = useRef(new Map<string, { at: number; since: number; d: Lead }>());
  // The open chat's own row, kept while the list on screen does not hold it (the other network, a filter, a quick
  // list): its ad, its picture, who set the progress are the chat's, not the list's. Without it the header and the
  // first line's ad card fell back to the bare headline and a grey picture (QA 2026-10-25 Q-07).
  const keptRow = useRef<LeadRow | undefined>(undefined);
  const remember = useCallback((waID: string, d: Lead, since = Date.now()) => {
    const m = opened.current;
    m.delete(waID);
    m.set(waID, { at: Date.now(), since, d });
    while (m.size > 40) m.delete(m.keys().next().value!);
  }, []);
  // Read a chat the pointer rests on, so the click opens what is already here.
  const peekTimer = useRef(0);
  const peek = useCallback(
    (waID: string, wait = 120) => {
      window.clearTimeout(peekTimer.current);
      const had = opened.current.get(waID);
      if (had && Date.now() - had.at < 30_000) return;
      peekTimer.current = window.setTimeout(() => {
        api
          .lead(waID, true)
          .then((d) => remember(waID, d))
          .catch(() => undefined);
      }, wait);
    },
    [remember],
  );
  useEffect(() => () => window.clearTimeout(peekTimer.current), []);

  const loadDetail = useCallback(
    async (waID: string, opts: { reportErrors?: boolean } = {}) => {
      const ticket = detailSeq.next();
      try {
        const d = await api.lead(waID);
        // Most polls bring the chat back unchanged but for its picture's new
        // signature. The copy already here then stays, on screen and in the
        // cache, and the open chat is not drawn again — while it is young
        // enough that its links still work for a picture drawn from it later.
        const had = opened.current.get(waID);
        const keep = had && Date.now() - had.since < LINK_FRESH_MS && rowSig(had.d) === rowSig(d) ? had : null;
        remember(waID, keep ? keep.d : d, keep?.since);
        if (!detailSeq.isCurrent(ticket) || selectedRef.current !== waID) return;
        chatPulse.current = { wa: waID, tag: d.pulse ?? "" };
        setDetail(keep ? keep.d : d);
        setDetailError("");
      } catch (e) {
        if (!detailSeq.isCurrent(ticket) || selectedRef.current !== waID) return;
        if (e instanceof ApiError && e.status === 401) return;
        // On open, showing the PREVIOUS lead would be dangerous: the actions in
        // the drawer (record sale, qualify, status) would target the wrong
        // customer. A failed background refresh may safely keep the pane.
        if (opts.reportErrors) {
          setDetail(null);
          setDetailError("读取失败。");
        }
      }
    },
    [detailSeq, remember],
  );

  useEffect(() => {
    void loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    if (!selected) {
      setDetail(null);
      setDetailError("");
      return;
    }
    // The pane changes at once: to this customer as last read, else to a
    // place holding their name. The previous customer never stays on screen
    // under this one's highlight — the drawer's buttons would act on them.
    const had = opened.current.get(selected);
    setDetail(had ? had.d : null);
    setDetailError("");
    void loadDetail(selected, { reportErrors: !had });
  }, [selected, loadDetail]);

  // 先回这几位 (no chat open) names whoever waits on either network, whichever list is on screen: it named the
  // list's own, and under 人工回复 or Messenger said 「没有客户在等回复」 while the menu counted one (QA G2-10).
  // null until it has answered once. Quiet: a convenience beside the list, never what the page waits for.
  const [waiting, setWaiting] = useState<LeadRow[] | null>(null);
  const waitSeq = useLatest();
  const loadWaiting = useCallback(() => {
    if (document.hidden) return;
    const ticket = waitSeq.next();
    api
      .leads({ ...EMPTY, reply: true }, WAITING_MAX, true)
      .then((d) => waitSeq.isCurrent(ticket) && setWaiting(d.leads ?? []))
      .catch(() => undefined);
  }, [waitSeq]);

  const refresh = useCallback(() => {
    if (document.hidden) return;
    void loadLeads();
    if (selectedRef.current) void loadDetail(selectedRef.current);
    else loadWaiting();
  }, [loadLeads, loadDetail, loadWaiting]);

  // Whether the API answers the pulse; an older one does not, and the page reads everything every 15 s as it did.
  const [pulsing, setPulsing] = useState(true);
  useInterval(refresh, 15000, active && !pulsing);
  useOnReturn(refresh, active && !pulsing);

  // How many are on each work list, shown on its button: whether anything is
  // waiting should not take opening five lists to find out. Asked per network,
  // so a button counts the network the list shows, and 全部 is the two added
  // up, with the split on hover — the customer table shows one at a time.
  const [counts, setCounts] = useState<{ whatsapp: Record<string, number>; messenger: Record<string, number> }>({
    whatsapp: {},
    messenger: {},
  });
  // A background tab skips the read, and asks the moment it is back if it did.
  const countsSkipped = useRef(false);
  const loadCounts = useCallback(() => {
    countsSkipped.current = document.hidden;
    if (document.hidden) return;
    // Quiet: numbers on the buttons, never what a page waits for, and read every
    // minute from any page — one landing just after a press kept that page's
    // line and menu item turning (up to 2.3 s on production).
    Promise.all([api.leadCounts("whatsapp", true), api.leadCounts("messenger", true)])
      .then(([whatsapp, messenger]) => {
        setCounts({ whatsapp, messenger });
        onWaiting?.((whatsapp.reply ?? 0) + (messenger.reply ?? 0));
        if (onCounts) {
          const both: Record<string, number> = { ...whatsapp };
          for (const [k, n] of Object.entries(messenger)) both[k] = (both[k] ?? 0) + n;
          onCounts(both);
        }
      })
      .catch(() => {
        /* the numbers are a convenience; the lists still work without them */
      });
  }, [onWaiting, onCounts]);
  const countOf = (k: string) =>
    filters.network === "whatsapp"
      ? counts.whatsapp[k] ?? 0
      : filters.network === "messenger"
        ? counts.messenger[k] ?? 0
        : (counts.whatsapp[k] ?? 0) + (counts.messenger[k] ?? 0);
  const splitOf = (k: string) =>
    filters.network ? "" : `\nWhatsApp ${counts.whatsapp[k] ?? 0} · Messenger ${counts.messenger[k] ?? 0}`;
  // A hand-back moves 人工回复's number at once; the counts are read again
  // within the minute, and the server keeps them for twenty seconds.
  const adjustHeld = useCallback((rows: LeadRow[], by: number) => {
    setCounts((c) => {
      const n = { whatsapp: { ...c.whatsapp }, messenger: { ...c.messenger } };
      for (const l of rows) {
        const net = isMessenger(l) ? n.messenger : n.whatsapp;
        net.held = Math.max(0, (net.held ?? 0) + by);
      }
      return n;
    });
  }, []);
  useEffect(() => {
    if (active) loadCounts();
  }, [active, loadCounts]);
  // Every minute on any page, not only this one: the phone bar's 对话 carries
  // the number waiting, and it is read from 客户表 and 报表 as well.
  useInterval(loadCounts, 60000, true);
  useEffect(() => {
    if (!active) loadCounts(); // once at the start, when the console opens on another page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // A console opened in a background tab had no numbers until the next minute,
  // and no number reads as nobody waiting (2026-10-21). Shown again, it asks at
  // once — only if a read was skipped, so a tab in front asks no more often.
  useOnVisible(() => countsSkipped.current && loadCounts(), true);

  // Every PULSE_MS while 对话 is in front of somebody, the server is asked whether anything changed (api.leadsPulse,
  // about a millisecond's work there), and the list or the open chat is read only when its tag has moved. The page
  // read both whole every 15 seconds, so 人工回复 or 交回小爱 pressed on one screen stayed as it was on the next for up
  // to that long, and a customer's line came as late (owner, 2026-10-28: 「一个cs按了其他cs的画面没变？…对话显示有点
  // 延迟？」). The buttons' numbers follow at most every COUNTS_MS: the server keeps them that long anyway.
  const countsAt = useRef(0);
  const beat = useCallback(() => {
    if (document.hidden) return;
    // The whole read once the list is FULL_MS old, for what the pulse does not see.
    if (Date.now() - listReadAt.current >= FULL_MS) {
      refresh();
      return;
    }
    const open = selectedRef.current ?? "";
    api
      .leadsPulse(open)
      .then((p) => {
        if (p.all !== listPulse.current && Date.now() >= listHeld.current) {
          void loadLeads();
          if (!selectedRef.current) loadWaiting();
          if (Date.now() - countsAt.current >= COUNTS_MS) {
            countsAt.current = Date.now();
            loadCounts();
          }
        }
        const had = chatPulse.current;
        if (open && open === selectedRef.current && (had?.wa !== open || had.tag !== (p.open ?? ""))) void loadDetail(open);
      })
      .catch((e) => {
        if (e instanceof ApiError && (e.status === 404 || e.status === 405 || e.status === 501)) setPulsing(false);
      });
  }, [refresh, loadLeads, loadDetail, loadWaiting, loadCounts]);
  // At once as well when 对话 is shown again, or comes back in front of somebody — by tab or by window — so a press on
  // another device shows without waiting for the next beat.
  useInterval(beat, PULSE_MS, active && pulsing);
  useOnReturn(beat, active && pulsing);
  // 先回这几位 is read when it comes on screen — no chat open — and again when the menu's number moves.
  const waitingN = (counts.whatsapp.reply ?? 0) + (counts.messenger.reply ?? 0);
  useEffect(() => {
    if (active && !selected) loadWaiting();
  }, [active, selected, waitingN, loadWaiting]);

  // Opening a chat changes the pane in the same render as the highlight: left
  // to the effect above, one frame still showed the customer before.
  const show = useCallback((waID: string) => {
    if (selectedRef.current !== waID) {
      const had = opened.current.get(waID);
      setDetail(had ? had.d : null);
      setDetailError("");
    }
    setSelected(waID);
  }, []);

  useEffect(() => {
    if (!openRequest) return;
    show(openRequest.wa);
    setDetailOpen(true);
    setBackFor(openRequest.from === "followup" ? openRequest.wa : null);
  }, [openRequest, show]);
  // A computer opens on a chat — the list's first — as the prototype does (v2.20 ChatPage); the side beside the list
  // was 75% of the screen and empty (owner 10/24). A phone opens on the list. With nothing in the list, 先回这几位
  // (FirstReplies) names whoever waits instead. Opened before 对话 is shown (reading a chat marks nothing read), so
  // its layout is made off screen with the list's: opened on the first showing, the whole chat was laid out in that
  // one frame, 0.25-0.43 s (10/25).
  useEffect(() => {
    if (selected || !listRead || leads.length === 0 || window.matchMedia("(max-width: 800px)").matches) return;
    show(leads[0]!.wa_id);
  }, [selected, listRead, leads, show]);
  const drawnOnce = useRef(false);
  useEffect(() => {
    if (!detail || active || drawnOnce.current) return;
    drawnOnce.current = true;
    onChatDrawn?.();
  }, [detail, active, onChatDrawn]);
  // 「‹ 回到跟进」 (prototype v2.20 .chat-fuback): on the chat 跟进 opened, until it is pressed, another chat is
  // opened or the page is left another way — the way back is then gone (App's backTo).
  const [backFor, setBackFor] = useState<string | null>(null);
  useEffect(() => {
    if (!active) setBackFor(null);
  }, [active]);
  const goBack = useCallback(() => {
    setBackFor(null);
    onBackTo?.();
  }, [onBackTo]);

  // Steady, as the rows' other handlers are, so a row is drawn again only
  // when its own chat changes (ChatRow is memo'd), and the open chat
  // (LeadDetail, memo'd too) only when it does.
  const openLead = useCallback(
    (waID: string) => {
      show(waID);
      setDetailOpen(true);
    },
    [show],
  );
  const unpeek = useCallback(() => window.clearTimeout(peekTimer.current), []);
  const detailChanged = useCallback(() => {
    void loadLeads();
    if (selectedRef.current) void loadDetail(selectedRef.current);
  }, [loadLeads, loadDetail]);

  /** Hand a blob to the browser as a download. Revoking in the same tick can
   *  cancel a download that has only been queued (Firefox/Safari). */
  function download(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  async function exportDisqualified() {
    setExportNote("");
    try {
      const name = `不合格-${new Date().toISOString().slice(0, 10)}.csv`;
      download(await api.exportDisqualified(), name);
      toast(`下载了不合格号码：${name}，上传到 Meta 当排除名单`, "win");
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) setExportNote("导出失败：" + (e as Error).message);
    }
  }

  async function exportCsv() {
    setExporting(true);
    setExportNote("");
    try {
      const { blob, truncated } = await api.exportCsv(filters);
      if (truncated) setExportNote(`导出最多 ${truncated} 行，其余的请缩小筛选再导。`);
      download(blob, `leads-${new Date().toISOString().slice(0, 10)}.csv`);
      toast(listCount && !truncated ? `导出了 ${listCount.n} 个对话` : "导出了");
    } catch (e) {
      if (!(e instanceof ApiError && e.status === 401)) setExportNote("导出失败：" + (e as Error).message);
    } finally {
      setExporting(false);
    }
  }

  const set = <K extends keyof LeadFilters>(k: K, v: LeadFilters[K]) => setFilters((f) => ({ ...f, [k]: v }));

  /**
   * 交回小爱: the chat header's 小爱回复, for one row or the whole list on
   * screen, without opening each chat. A colleague's reply pauses 小爱 in that
   * chat and she stays paused until somebody presses 小爱回复 (owner,
   * 2026-10-06) — right while they are holding it, a chore once they are done,
   * one chat at a time after a group send. A colleague's next reply pauses
   * each one again, so it is not a one-way door, and 撤回 takes it back.
   */
  async function handBack(rows: LeadRow[]) {
    setAskBulk(false);
    setUndo(null);
    if (rows.length === 0) return;
    listHeld.current = Date.now() + 60_000;
    setHanded((h) => ({ ...h, ...Object.fromEntries(rows.map((l) => [l.wa_id, "going" as const])) }));
    const many = rows.length > 1;
    if (many) setBulk({ done: 0, total: rows.length });
    const ok: LeadRow[] = [];
    let next = 0;
    let done = 0;
    // Four at a time: a group send's fifty chats in a couple of seconds.
    const worker = async () => {
      while (next < rows.length) {
        const l = rows[next++]!;
        try {
          await api.setBot(l.wa_id, true);
          ok.push(l);
          doneAt.current[l.wa_id] = Date.now();
          setHanded((h) => ({ ...h, [l.wa_id]: "done" }));
          addChatNote(l.wa_id, "交回小爱，她接着回");
        } catch {
          setHanded((h) => ({ ...h, [l.wa_id]: "failed" }));
        }
        done += 1;
        if (many) setBulk({ done, total: rows.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, rows.length) }, worker));
    setBulk(null);
    // The 人工回复 list's rows are its count; 同事在回 reaches back further.
    const held = !!filters.held;
    if (held) adjustHeld(ok, -1);
    const failed = rows.length - ok.length;
    const who = (l: LeadRow) => l.name || (isMessenger(l) ? "Messenger 客户" : phoneLabel(l.wa_id));
    if (ok.length > 0) {
      setUndo({
        text: many ? `${ok.length} 个对话交回小爱了${failed ? `，${failed} 个没成功` : ""}` : `${who(ok[0]!)} 交回小爱了`,
        was: ok,
        held,
      });
    }
    // The open chat's own switch says 小爱回复 now, not at the next refresh.
    const open = selectedRef.current;
    if (open && ok.some((l) => l.wa_id === open)) void loadDetail(open);
    // The ticks stay a moment, the rows fold away (from .6 s, chat.css wc-hb-fold), then the list is read again.
    listHeld.current = Date.now() + 1200;
    window.setTimeout(() => void loadLeads(), 1200);
  }

  /** 撤回: the chats just handed back go back to 人工回复. */
  async function takeBack({ was: rows, held }: { was: LeadRow[]; held: boolean }) {
    setUndo(null);
    await Promise.all(rows.map((l) => api.setBot(l.wa_id, false).catch(() => undefined)));
    setHanded((h) => {
      const n = { ...h };
      for (const l of rows) delete n[l.wa_id];
      return n;
    });
    setFolded((f) => {
      const n = new Set(f);
      for (const l of rows) n.delete(l.wa_id);
      return n;
    });
    for (const l of rows) addChatNote(l.wa_id, "撤回了，还是同事回");
    toast("撤回了，还是同事回");
    if (held) adjustHeld(rows, 1);
    void loadLeads();
    const open = selectedRef.current;
    if (open && rows.some((l) => l.wa_id === open)) void loadDetail(open);
  }

  // A row's 交回小爱: one steady handler for every row, running this
  // render's handBack.
  const handBackNow = useRef(handBack);
  useEffect(() => {
    handBackNow.current = handBack;
  });
  const handOne = useCallback((l: LeadRow) => void handBackNow.current([l]), []);

  // 撤回 is offered for ten seconds, as on 小爱's page.
  useEffect(() => {
    if (!undo) return;
    const t = window.setTimeout(() => setUndo(null), 10_000);
    return () => window.clearTimeout(t);
  }, [undo]);

  const quick: QuickKey | "" = QUICK.find((x) => filters[x.key])?.key ?? "";
  function pickQuick(k: QuickKey | "") {
    setFilters((f) => ({
      ...f,
      // 要真人 was moved up from 筛选 without this line, and pressing it did
      // nothing (owner, 2026-10-11: 「为什么有些我点了没反应？」).
      needs_human: k === "needs_human",
      reply: k === "reply",
      close: k === "close",
      chase: k === "chase",
      followup_missed: false,
      followup_today: false,
      aftersale_due: k === "aftersale_due",
      held: k === "held",
      paid_hint: false,
      cod: false,
    }));
  }
  // 谁在回: the chat header's 小爱回复 / 人工回复 switch, as a list. 人工处理中
  // was a chip with no other side, so 小爱's own chats could not be found
  // (owner, 2026-10-15: 「我现在要怎么找小爱」).
  const who = filters.bot_on ? "bot" : filters.bot_off ? "human" : "";
  const moreCount = [filters.product, filters.ad_id, who, filters.label].filter(Boolean).length + MORE_LISTS.filter((m) => filters[m.key]).length;
  function clearMore() {
    setFilters((f) => ({
      ...f,
      status: "",
      ad_id: "",
      product: "",
      source: "",
      origin: "",
      lead_stage: "",
      label: "",
      bot_off: false,
      bot_on: false,
      purchased: false,
      disqualify_hint: false,
    }));
  }
  // The empty list's 清除筛选: the search, the list picked and 筛选's, all.
  function clearAll() {
    setSearch("");
    pickQuick("");
    clearMore();
  }
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  // 筛选's products and ads, each with the number of chats its press would list (lead_facets.go), read while the
  // panel is open or one of them is picked (the bar over the list names it), again whenever the filter changes.
  // The numbers last read stay until the new ones land: a panel whose pills went blank and back on every press
  // would jump under the finger.
  const [facets, setFacets] = useState<LeadFacets | null>(null);
  const facetSeq = useLatest();
  const facetsAsked = useRef("");
  const loadFacets = useCallback(() => {
    facetsAsked.current = JSON.stringify(filters);
    const ticket = facetSeq.next();
    api
      .leadFacets(filters)
      .then((d) => facetSeq.isCurrent(ticket) && setFacets(d))
      .catch(() => {
        /* the numbers are a convenience; the list still filters without them */
      });
  }, [filters, facetSeq]);
  const wantFacets = moreOpen || !!filters.product || !!filters.ad_id;
  useEffect(() => {
    if (wantFacets) loadFacets();
  }, [wantFacets, loadFacets]);
  // Read ahead, so the panel's first opening draws its products and ads at once: they landed after it, and it was
  // laid out twice and grew under the pointer. Once in a quiet moment after the page is first shown, and again when
  // the pointer comes to 筛选 with the filter changed since.
  const facetsEarly = useRef(false);
  useEffect(() => {
    if (!active || facetsEarly.current) return;
    const t = window.setTimeout(() => {
      facetsEarly.current = true;
      loadFacets();
    }, 2500);
    return () => window.clearTimeout(t);
  }, [active, loadFacets]);
  const facetsAhead = () => facetsAsked.current !== JSON.stringify(filters) && loadFacets();
  const [adQ, setAdQ] = useState("");
  // Each product's first AD_FIRST ads, the running ones; the rest a press away (and all of them to a search).
  const [adsOpen, setAdsOpen] = useState<ReadonlySet<string>>(() => new Set());
  // 收起 stays under the pointer: the ads above it fold away and the panel scrolls up by what they took. Left as it
  // was, the button jumped off the top of the panel with them.
  const foldAnchor = useRef<{ el: HTMLElement; top: number } | null>(null);
  function foldAds(key: string, el: HTMLElement) {
    foldAnchor.current = { el, top: el.getBoundingClientRect().top };
    setAdsOpen((s) => {
      const n = new Set(s);
      n.delete(key);
      return n;
    });
  }
  useLayoutEffect(() => {
    const a = foldAnchor.current;
    if (!a) return;
    foldAnchor.current = null;
    const box = a.el.closest<HTMLElement>(".wc-fscroll");
    if (box && a.el.isConnected) box.scrollTop += a.el.getBoundingClientRect().top - a.top;
  }, [adsOpen]);
  // A product picked keeps an ad picked only while the ad is that product's: the two together list nobody else.
  function pickProduct(p: string) {
    setFilters((f) => {
      const product = f.product === p ? "" : p;
      const ad = facets?.ads.find((a) => a.ad_id === f.ad_id);
      return { ...f, product, ad_id: !product || ad?.product === product ? f.ad_id : "" };
    });
  }
  const clearPicked = () => setFilters((f) => ({ ...f, product: "", ad_id: "" }));

  // 「这条广告的对话」 from another page (chatJump.ts): that ad's chats, or one product's, and nothing else narrowing
  // them — so the count on the list is the ad's whole count. Taken at once if it came before this page started.
  useEffect(() => {
    const take = (j: ChatJump | null) => {
      if (!j) return;
      setSearch("");
      setNetChoice("");
      setFilters({ ...EMPTY, ad_id: j.ad ?? "", product: j.product ?? "" });
      setMoreOpen(false);
      pickFirst.current = true;
    };
    take(takeChatJump());
    const heard = (e: Event) => {
      takeChatJump();
      take((e as CustomEvent<ChatJump>).detail);
    };
    window.addEventListener(CHAT_JUMP, heard);
    return () => window.removeEventListener(CHAT_JUMP, heard);
  }, [setNetChoice]);
  useEffect(() => {
    if (!moreOpen) return;
    const close = (e: MouseEvent) => {
      if (!moreRef.current?.contains(e.target as Node)) setMoreOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setMoreOpen(false);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [moreOpen]);

  // The lists sit on one line that slides. A mouse has no sideways wheel, so
  // the wheel slides it; and the list picked (from 今天, say) is slid into view.
  const quickRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = quickRef.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX) || el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);
  useEffect(() => {
    const el = quickRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      const on = el.querySelector<HTMLElement>(".wc-chip.on");
      if (!on || !el.checkVisibility()) return;
      const a = el.getBoundingClientRect();
      const b = on.getBoundingClientRect();
      if (b.left < a.left + 24) el.scrollLeft -= a.left + 24 - b.left;
      else if (b.right > a.right - 24) el.scrollLeft += b.right - (a.right - 24);
    });
    return () => cancelAnimationFrame(id);
  }, [quick]);

  // A dealer, a dealer's customer or progress 代理 is never handed to 小爱
  // (ai_off): the server refuses, and 全部交回小爱 does not count them.
  const botOffRows = leads.filter((l) => !l.bot_enabled && !l.ai_off && handed[l.wa_id] !== "done");
  // On its own list, 人工回复 counts the rows under it.
  const chipCount = (k: string) =>
    k === "held" && filters.held && heldShown && leads.length < listLimit(filters) ? botOffRows.length : countOf(k);
  // 交回小爱 is offered on the two lists of chats a colleague holds: 人工回复
  // (the last day's) and 筛选's 同事在回 (all of them).
  const handing = (!!filters.held || filters.bot_off) && can(me, "edit_conversation");
  // No timestamp at all counts as quiet: nothing has happened on it to hold.
  const quietRows = botOffRows.filter((l) => {
    const at = l.last_activity_at ? new Date(l.last_activity_at).getTime() : 0;
    return !at || Date.now() - at > 3 * 864e5;
  });

  // The mark after the last row drawn: seen within AHEAD of the list, it draws
  // the next rows, and is watched again after each step, so a long fling keeps
  // drawing until it is out of reach.
  const listBox = useRef<HTMLDivElement>(null);
  const listEnd = useRef<HTMLDivElement>(null);

  // 交回小爱's row, once its tick has shown, fades (chat.css wc-hb-fold) and is taken out; the rows under it slide
  // up into its place from where they stood (prototype chat.css .chat-row.handed, by transform only). Measured on
  // the press's own path, never while the page comes on screen.
  const flipFrom = useRef<Map<HTMLElement, number> | null>(null);
  const foldAway = useCallback((wa: string) => {
    const box = listBox.current;
    const tops = new Map<HTMLElement, number>();
    if (box && !stillWanted()) {
      const end = box.getBoundingClientRect().bottom;
      let below = false;
      for (const el of box.querySelectorAll<HTMLElement>(".wc-row")) {
        if (el.dataset.wa === wa) below = true;
        else if (below) {
          const top = el.getBoundingClientRect().top;
          if (top > end) break;
          tops.set(el, top);
        }
      }
    }
    flipFrom.current = tops;
    setFolded((f) => new Set(f).add(wa));
  }, []);
  useLayoutEffect(() => {
    const tops = flipFrom.current;
    if (!tops) return;
    flipFrom.current = null;
    for (const [el, was] of tops) {
      if (!el.isConnected) continue;
      const d = was - el.getBoundingClientRect().top;
      if (Math.abs(d) > 0.5) el.animate([{ transform: `translateY(${d}px)` }, { transform: "none" }], { duration: 300, easing: "cubic-bezier(.2, .8, .2, 1)" });
    }
  }, [folded]);
  const moreRows = leads.length > drawn;
  useEffect(() => {
    if (!moreRows || !listEnd.current) return;
    const seen = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setDrawn((n) => n + MORE_ROWS), {
      root: listBox.current,
      rootMargin: AHEAD,
    });
    seen.observe(listEnd.current);
    return () => seen.disconnect();
  }, [moreRows, drawn]);

  // Which network the screen is dressed as: the open conversation decides,
  // and with none open the tab does. A Messenger conversation is drawn the
  // way Messenger draws it — to the people answering them these are two
  // different products, and one look pretending to be both was rejected.
  const fbLook = selected ? selected.startsWith("fb:") : filters.network === "messenger";

  // 客户详情 as a column beside the chat when the chat keeps 640 beside it
  // (prototype v2.20), folded or not as last left; over the chat otherwise.
  const wcRef = useRef<HTMLDivElement>(null);
  const [col, setCol] = useState(() => roomForPane(window.innerWidth - 64 - 24));
  useLayoutEffect(() => {
    const el = wcRef.current;
    if (!el) return;
    const seen = new ResizeObserver(([e]) => {
      // 0 while the page is hidden (content-visibility): keep what it was.
      if (e && e.contentRect.width > 0) setCol(roomForPane(e.contentRect.width));
    });
    seen.observe(el);
    return () => seen.disconnect();
  }, []);
  const [paneOn, setPaneOn] = useState(() => {
    try {
      return localStorage.getItem(PANE_KEY) !== "0";
    } catch {
      return true;
    }
  });
  const pickPane = useCallback((on: boolean) => {
    setPaneOn(on);
    try {
      localStorage.setItem(PANE_KEY, on ? "1" : "0");
    } catch {
      /* remembered for this visit only */
    }
  }, []);
  // 客户详情 (CustomerCard) is a file of its own: fetched in a quiet moment, so the first chat opens with it.
  useEffect(() => {
    const t = window.setTimeout(preloadCard, 1500);
    return () => window.clearTimeout(t);
  }, []);

  // 筛选's products: those that would list somebody, and the one picked.
  const productChips = (facets?.products ?? []).filter((p) => p.n > 0 || p.key === filters.product);
  // Its ads: the picked product's, else each product's under its name; and the search's, past AD_SEARCH of them.
  const needle = adQ.trim().toLowerCase();
  const adsShown = (facets?.ads ?? []).filter(
    (a) =>
      (!filters.product || a.product === filters.product) &&
      (!needle || [a.headline, a.ad_name, a.campaign, a.ad_id].some((s) => s?.toLowerCase().includes(needle))),
  );
  // Each group's first AD_FIRST — Meta's running ones come first — and the picked one; all of them once unfolded or
  // searched. A whole account's ads, with their pictures, made the panel's first opening the slowest frame on the page.
  const adGroups = (
    filters.product
      ? [{ key: filters.product, ads: adsShown }]
      : (facets?.products ?? []).map((p) => ({ key: p.key, ads: adsShown.filter((a) => a.product === p.key) }))
  )
    .filter((g) => g.ads.length > 0)
    .map((g) => {
      // folds: unfolded by a press, so 收起 can put it back (a search shows them all, and its end is the way back).
      const folds = !needle && g.ads.length > AD_FIRST + 1;
      if (!folds || adsOpen.has(g.key)) return { ...g, shown: g.ads, folds };
      const first = g.ads.slice(0, AD_FIRST);
      const picked = g.ads.find((a) => a.ad_id === filters.ad_id && !first.includes(a));
      return { ...g, shown: picked ? [...first, picked] : first, folds };
    });
  // The ad picked, for the bar over the list: as 筛选 read it, else as its chats carry it.
  const pickedRow = filters.ad_id ? leads.find((l) => l.ad_id === filters.ad_id) : undefined;
  const pickedAd: Pick<FacetAd, "ad_id" | "headline" | "ad_name" | "thumb" | "product"> | undefined = filters.ad_id
    ? (facets?.ads.find((a) => a.ad_id === filters.ad_id) ??
      { ad_id: filters.ad_id, headline: pickedRow?.ad_headline, thumb: pickedRow?.ad_thumb, product: filters.product ?? "" })
    : undefined;

  const listRow = detail ? leads.find((l) => l.wa_id === detail.wa_id) : undefined;
  if (listRow) keptRow.current = listRow;
  const openRow = listRow ?? (detail && keptRow.current?.wa_id === detail.wa_id ? keptRow.current : undefined);

  return (
    <div ref={wcRef} className={`wc${detailOpen ? " open" : ""}${fbLook ? " fb" : ""}`}>
      <div className="wc-side">
        {/* The list's top (prototype v2.20): search, the network and 筛选 on
            one line. The page's name is the frame's (the sidebar, the phone's
            top bar), so it is not said again here. */}
        <div className="wc-search">
          <div className="wc-search-box">
            <ShellIcon name="search" size={16} />
            <input
              type="search"
              placeholder="搜名字或号码"
              aria-label="搜名字或号码"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="wc-nets" role="tablist" aria-label="渠道">
            {NETWORKS.map((n) => (
              <button
                type="button"
                role="tab"
                aria-selected={filters.network === n.key}
                aria-label={n.key ? n.label : "全部渠道"}
                title={n.key ? `只看 ${n.label}` : "全部渠道"}
                key={n.key}
                className={`wc-net-tab${filters.network === n.key ? " on" : ""}`}
                onClick={() => {
                  set("network", n.key);
                  setNetChoice(n.key);
                }}
              >
                {n.icon ?? n.label}
              </button>
            ))}
          </div>
          {/* Everything rarer than the lists below: one button, a count of what
              is switched on, a panel on click. Eleven chips and four menus on
              the list itself were the 乱 the team pointed at. */}
          <div className="wc-more" ref={moreRef}>
            <button
              type="button"
              className={`wc-more-btn${moreCount ? " on" : ""}`}
              aria-expanded={moreOpen}
              aria-label={moreCount ? `筛选，已选 ${moreCount} 项` : "筛选"}
              title="筛选"
              onPointerEnter={facetsAhead}
              onFocus={facetsAhead}
              onClick={() => setMoreOpen((v) => !v)}
            >
              <ShellIcon name="filter" />
              {moreCount > 0 && <b>{moreCount}</b>}
            </button>
            {/* The prototype's panel (chat.js ChatFilters): each choice a row of pills — a product with its colour,
                an ad as a row with its picture — and 看 N 个对话 at the foot, the list's own count as the pills change. */}
            {moreOpen && (
              <div className="wc-more-panel" role="dialog" aria-label="筛选">
                <div className="wc-fscroll">
                  {/* 产品 first (owner, 2026-10-27: 「我如果要知道比如谷粮也找不到对应顾客」): every product that would list
                      somebody, its number the list its press shows — counted on the server over every chat, not the 200
                      on screen. A product is an ad's by 经营's rule, else the chat's own; 代餐 comes in with its first chat. */}
                  {productChips.length > 0 && (
                    <div className="wc-fg">
                      <div className="wc-fg-t">产品</div>
                      <div className="wc-fg-o">
                        {productChips.map((p) => (
                          <button
                            key={p.key}
                            type="button"
                            title={`${productName(p.key)}：点的广告是${productName(p.key)}的；没有广告的，看对话本身的产品`}
                            aria-pressed={filters.product === p.key}
                            className={`wc-chip${filters.product === p.key ? " on" : ""}`}
                            onClick={() => pickProduct(p.key)}
                          >
                            <span className="wc-prog" style={{ "--pc": productColor(p.key) } as CSSProperties}>
                              {productName(p.key)}
                            </span>
                            <b>{p.n}</b>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* The ads, as 经营's ad list shows them — picture, name, campaign — each with its number of chats, under
                      its product (the picked product's only), the ones Meta has on first. It was a dropdown of names past
                      eight ads, and only for those who may read the report. */}
                  {(facets?.ads.length ?? 0) > 0 && (
                    <div className="wc-fg">
                      <div className="wc-fg-t">点的广告</div>
                      {(facets?.ads.length ?? 0) > AD_SEARCH && (
                        <label className="wc-ads-s">
                          <ShellIcon name="search" size={15} />
                          <input value={adQ} onChange={(e) => setAdQ(e.target.value)} placeholder="搜广告名字" aria-label="搜广告名字" spellCheck={false} />
                        </label>
                      )}
                      <div className="wc-ads">
                        {adGroups.map((g) => (
                          <div key={g.key} className="wc-adg" role="group" aria-label={productName(g.key)}>
                            {!filters.product && (
                              <div className="wc-adg-t">
                                <span className="wc-prog" style={{ "--pc": productColor(g.key) } as CSSProperties}>
                                  {productName(g.key)}
                                </span>
                              </div>
                            )}
                            {g.shown.map((a) => {
                              const on = filters.ad_id === a.ad_id;
                              const name = facetAdName(a);
                              return (
                                <button
                                  key={a.ad_id}
                                  type="button"
                                  title={a.campaign ? `${name}\n${a.campaign}` : name}
                                  aria-pressed={on}
                                  className={`wc-ado${on ? " on" : ""}`}
                                  onClick={() => set("ad_id", on ? "" : a.ad_id)}
                                >
                                  <AdPic src={a.thumb} size={36} />
                                  <span className="wc-ado-t">
                                    <b>{name}</b>
                                    {(a.running || (a.campaign && ownName(a))) && (
                                      <small>
                                        {a.running && (
                                          <span className="wc-run">
                                            <i />
                                            在跑
                                          </span>
                                        )}
                                        {a.campaign && ownName(a) && <span className="wc-ado-c">{adLabel(a.campaign)}</span>}
                                      </small>
                                    )}
                                  </span>
                                  <em title={`${a.n} 个对话`}>{a.n}</em>
                                </button>
                              );
                            })}
                            {/* Unfolded, the same button folds them again: it only opened, and a long product's
                                ads then filled the panel for good (owner, 2026-10-28: 「展开了就收不回来了？」). */}
                            {g.ads.length > g.shown.length ? (
                              <button type="button" className="wc-ads-more" onClick={() => setAdsOpen((s) => new Set(s).add(g.key))}>
                                还有 {g.ads.length - g.shown.length} 条广告
                                <ShellIcon name="down" size={14} />
                              </button>
                            ) : (
                              g.folds && (
                                <button type="button" className="wc-ads-more" onClick={(e) => foldAds(g.key, e.currentTarget)}>
                                  收起
                                  <ShellIcon name="up" size={14} />
                                </button>
                              )
                            )}
                          </div>
                        ))}
                        {adQ.trim() && adGroups.length === 0 && <div className="wc-ads-none">没有叫这个名字的广告</div>}
                      </div>
                    </div>
                  )}
                  <div className="wc-fg">
                    <div className="wc-fg-t">谁在回</div>
                    <div className="wc-fg-o">
                      {WHO.map((w) => (
                        <button
                          key={w.value}
                          type="button"
                          title={w.title}
                          aria-pressed={who === w.value}
                          className={`wc-chip${who === w.value ? " on" : ""}`}
                          onClick={() => setFilters((f) => ({ ...f, bot_on: who !== w.value && w.value === "bot", bot_off: who !== w.value && w.value === "human" }))}
                        >
                          <ShellIcon name={w.icon} size={14} />
                          {w.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="wc-fg">
                    <div className="wc-fg-t">名单</div>
                    <div className="wc-fg-o">
                      {MORE_LISTS.map((m) => (
                        <button
                          key={m.key}
                          type="button"
                          title={m.title}
                          aria-pressed={!!filters[m.key]}
                          className={`wc-chip${filters[m.key] ? " on" : ""}`}
                          onClick={() => set(m.key, !filters[m.key])}
                        >
                          {m.label}
                          {countOf(m.key) ? <b>{countOf(m.key)}</b> : null}
                        </button>
                      ))}
                      {/* Labels are the team's own words; a chip exists only once
                          somebody has used one. */}
                      {labelsInUse.map((lb) => (
                        <button
                          key={lb}
                          type="button"
                          title={`带「${lb}」标签的对话`}
                          aria-pressed={filters.label === lb}
                          className={`wc-chip${filters.label === lb ? " on" : ""}`}
                          onClick={() => set("label", filters.label === lb ? "" : lb)}
                        >
                          # {lb}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="wc-ff">
                  <Btn kind="ghost" size="sm" disabled={moreCount === 0} onClick={clearMore}>
                    清除筛选
                  </Btn>
                  {/* The exclusion audience, once the judging is done. It showed
                      only while Meta 阶段 was 不合格, and that picker is gone. */}
                  <Btn kind="ghost" size="sm" icon="download" title="下载不合格的号码，上传到 Meta 当排除名单" onClick={() => void exportDisqualified()}>
                    不合格号码
                  </Btn>
                  {can(me, "export_csv") && (
                    <Btn kind="ghost" size="sm" icon="download" disabled={exporting} title="把筛选出来的列表下载成 CSV" onClick={() => void exportCsv()}>
                      导出
                    </Btn>
                  )}
                  <Btn kind="primary" size="sm" onClick={() => setMoreOpen(false)}>
                    看 {listCount ? `${listCount.n}${listCount.capped ? "+" : ""}` : leads.length} 个对话
                  </Btn>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* The lists a colleague works down, one at a time, as WhatsApp's own
            All / Unread chips: pick one, the list is that. One line that
            slides; a mouse wheel slides it too. */}
        <div className="wc-quick" ref={quickRef}>
          <button type="button" className={`wc-chip${quick === "" ? " on" : ""}`} onClick={() => pickQuick("")}>
            全部
          </button>
          {QUICK.map((x) => (
            <button
              key={x.key}
              type="button"
              title={x.title + splitOf(x.key)}
              className={`wc-chip${quick === x.key ? " on" : ""}${x.key === "needs_human" && countOf(x.key) ? " hot" : ""}`}
              onClick={() => pickQuick(quick === x.key ? "" : x.key)}
            >
              {x.label}
              {chipCount(x.key) ? <b>{chipCount(x.key)}</b> : null}
            </button>
          ))}
        </div>
        {/* What the list is narrowed to, by product or ad, and how many: one line over it, its ✕ the way back. An ad's
            chats opened from 经营 land here with nothing else said about them. */}
        {(filters.product || pickedAd) && (
          <div className="wc-picked">
            {pickedAd ? (
              <AdPic src={pickedAd.thumb} size={26} />
            ) : (
              <i className="wc-picked-dot" style={{ "--pc": productColor(filters.product ?? "") } as CSSProperties} />
            )}
            <button type="button" className="wc-picked-t" title="换一个" onClick={() => setMoreOpen(true)}>
              {pickedAd ? facetAdName(pickedAd) : productName(filters.product ?? "")}
            </button>
            {/* The count of this list, once it is read: the one before stood here a moment after each press. */}
            <b className="wc-picked-n">{listCount?.of === JSON.stringify(filters) ? `${listCount.n}${listCount.capped ? "+" : ""} 个对话` : "…"}</b>
            <button type="button" className="wc-picked-x" title={pickedAd ? "不只看这条广告" : "不只看这个产品"} aria-label={pickedAd ? "不只看这条广告" : "不只看这个产品"} onClick={clearPicked}>
              <ShellIcon name="x" size={14} />
            </button>
          </div>
        )}
        {/* The list's one action, on top of it: everything under it back to 小爱. */}
        {handing && (botOffRows.length > 0 || bulk) && (
          <div className={`wc-hb${bulk ? " busy" : ""}`}>
            <span className="wc-hb-t">
              {bulk ? (
                "正在交回小爱…"
              ) : (
                <>
                  <b>{botOffRows.length}</b> 个对话同事在回，小爱不回
                </>
              )}
            </span>
            {bulk ? (
              <span className="wc-hb-go" aria-live="polite">
                {bulk.done} / {bulk.total}
              </span>
            ) : (
              <button type="button" className="wc-hb-go" onClick={() => setAskBulk(true)}>
                <ShellIcon name="sparkles" size={14} /> 全部交回小爱
              </button>
            )}
            {bulk && <i className="wc-hb-bar" style={{ width: `${(bulk.done / bulk.total) * 100}%` }} />}
          </div>
        )}
        {listError && <div className="wc-band">{listError}</div>}
        {exportNote && <div className="wc-band">{exportNote}</div>}

        <div className="wc-list" ref={listBox}>
          {!listRead ? (
            <PageSkel page="leads" bare />
          ) : leads.length === 0 && filters.held && !search.trim() ? (
            <div className="wc-empty wc-hb-empty">
              <ShellIcon name="sparkles" size={20} />
              24 小时内没有同事在回的对话
            </div>
          ) : leads.length === 0 ? (
            // Nothing matches: say so, and the way back to the whole list
            // (prototype v2.20). A search that found nobody counts too.
            quick || moreCount || search.trim() ? (
              <Empty
                icon="search"
                line="没有符合的对话"
                action={
                  <Btn size="sm" onClick={clearAll}>
                    清除筛选
                  </Btn>
                }
              />
            ) : (
              <Empty icon="chat" line="还没有对话" />
            )
          ) : (
            <>
              {leads.slice(0, drawn).map((l) =>
                folded.has(l.wa_id) ? null : (
                <ChatRow
                  key={l.wa_id}
                  l={l}
                  on={selected === l.wa_id}
                  owner={owners[l.wa_id]}
                  handing={handing}
                  handed={handed[l.wa_id]}
                  today={today}
                  onOpen={openLead}
                  onPeek={peek}
                  onUnpeek={unpeek}
                  onHandBack={handOne}
                  onFolded={foldAway}
                />
                ),
              )}
              {moreRows && <div ref={listEnd} aria-hidden="true" />}
            </>
          )}
        </div>
      </div>

      {/* The question before the whole list goes (prototype ChatBulkDialog): who goes back, their faces. */}
      {askBulk && (
        <ChatModal
          title={`把 ${botOffRows.length} 个对话交回小爱？`}
          sub="人工回复"
          onClose={() => setAskBulk(false)}
          foot={
            <>
              <Btn kind="ghost" onClick={() => setAskBulk(false)}>
                取消
              </Btn>
              {filters.bot_off && quietRows.length > 0 && quietRows.length < botOffRows.length && (
                <Btn onClick={() => void handBack(quietRows)}>只交回 3 天没动静的 {quietRows.length} 个</Btn>
              )}
              <Btn kind="primary" icon="sparkles" onClick={() => void handBack(botOffRows)}>
                交回小爱
              </Btn>
            </>
          }
        >
          <p className="wc-modal-p">客户再发消息就由小爱回；你再回其中哪个，哪个又转人工。代理和代理的顾客不算在里面。</p>
          <div className="wc-bulk">
            {botOffRows.slice(0, BULK_FACES).map((l) => (
              <span key={l.wa_id} className="wc-bulk-p">
                <span className="wc-av" style={avatarStyle(l.wa_id)} aria-hidden="true">
                  <AvatarPic url={l.avatar_url} />
                  {initialOf(l.name || l.wa_id)}
                  <NetDot fb={isMessenger(l)} />
                </span>
                <small>{l.name || (isMessenger(l) ? "Messenger 客户" : phoneLabel(l.wa_id))}</small>
              </span>
            ))}
            {botOffRows.length > BULK_FACES && <span className="wc-bulk-more">还有 {botOffRows.length - BULK_FACES} 个</span>}
          </div>
        </ChatModal>
      )}

      {/* 撤回 for ten seconds, a dark bar that rises from the bottom (prototype .chat-undo). */}
      {undo && (
        <div className="wc-undo" role="status">
          <ShellIcon name="check" size={16} />
          <span>{undo.text}</span>
          <button type="button" onClick={() => void takeBack(undo)}>
            撤回
          </button>
        </div>
      )}

      {detail ? (
        <LeadDetail
          key={detail.wa_id}
          lead={detail}
          row={openRow}
          me={me}
          paneRef={paneRef}
          onChanged={detailChanged}
          onBack={closeChat}
          onOpen={openLead}
          col={col}
          paneOn={paneOn}
          onPane={pickPane}
          onBackTo={backFor === detail.wa_id ? goBack : undefined}
        />
      ) : selected && !detailError ? (
        <ChatLoading row={leads.find((l) => l.wa_id === selected)} waID={selected} onBack={closeChat} />
      ) : detailError || !listRead || !waiting ? (
        <div className="wc-blank">{detailError}</div>
      ) : (
        <FirstReplies rows={waiting} onOpen={openLead} />
      )}
    </div>
  );
}

interface RowProps {
  l: LeadRow;
  /** The chat open beside the list. */
  on: boolean;
  /** 负责的同事 (owners.tsx), if anybody. */
  owner?: string;
  /** The list offers 交回小爱 (人工回复, or 筛选's 同事在回). */
  handing: boolean;
  handed?: "going" | "done" | "failed";
  /** Not read here: a new day redraws the row, whose Day label counts from today. */
  today: number;
  onOpen: (waID: string) => void;
  onPeek: (waID: string, wait?: number) => void;
  onUnpeek: () => void;
  onHandBack: (l: LeadRow) => void;
  /** Its fold after 交回小爱 has played: it is taken out of the list. */
  onFolded: (waID: string) => void;
}

/**
 * One chat in the list, as WhatsApp Web's: the face, the name and time, the
 * last line, the one thing to do, the labels. Memo'd, with the chat kept as
 * the same object while the server sends it again unchanged: a poll redraws
 * the chats that changed, not 200.
 */
const ChatRow = memo(function ChatRow({ l, on, owner, handing, handed, onOpen, onPeek, onUnpeek, onHandBack, onFolded }: RowProps) {
  const p = previewOf({
    direction: "in",
    text: l.last_text,
    raw_type: "",
    at: l.last_activity_at ?? new Date().toISOString(),
  });
  const job = rowJob(l);
  // The labels the phone gives them, worked out (labels.tsx).
  const tags = [owner ? ownerLabel(owner) : null, leadProduct(l), leadStage(l)].filter((x): x is WaLabel => !!x);
  const again = askingAgain(l) ? "买过，又点了广告来问：照新询问算，Day 从点广告那天算" : undefined;
  // What they wore before the tap, faded after the new ones.
  const past = pastLabel(l);
  return (
    <div
      // Unread, as the prototype marks it (the dot, the time in jade, the name and line darker): here the customer
      // wrote and nobody has answered — the list has no per-colleague read marks.
      className={`wc-row${on ? " on" : ""}${l.unanswered ? " unread" : ""}${handing && handed === "done" ? " handed" : ""}`}
      data-wa={l.wa_id}
      onAnimationEnd={(e) => e.target === e.currentTarget && e.animationName === "wc-hb-fold" && onFolded(l.wa_id)}
      onPointerEnter={(e) => e.pointerType === "mouse" && onPeek(l.wa_id)}
      onPointerLeave={onUnpeek}
      onPointerDown={() => onPeek(l.wa_id, 0)}
      onClick={() => onOpen(l.wa_id)}
    >
      {/* The avatar makes a list scannable by shape rather than by
          reading every name: the person's own picture when the
          network hands one out, a coloured initial otherwise. The
          network is its dot (prototype v2.20). */}
      <span className="wc-av" style={avatarStyle(l.wa_id)} aria-hidden="true">
        <AvatarPic url={l.avatar_url} />
        {initialOf(l.name || l.wa_id)}
        <NetDot fb={isMessenger(l)} />
      </span>
      <div className="wc-rt">
        <div className="wc-r1">
          <span className="wc-name">
            <span className="wc-name-t">{l.name || (isMessenger(l) ? "Messenger 客户" : phoneLabel(l.wa_id))}</span>
            <DealerTag d={l.legacy_dealer} c={l.legacy_customer} />
            {/* The same person's other number: the chat is open and
                answerable here, but the customer is counted on the
                chat it was merged into. */}
            {l.merged_into && (
              <i className="wc-merged" title="同一个人的另一个号码，客户记在另一条对话">
                同一个人
              </i>
            )}
          </span>
          <span className="wc-time">{fmtListWhen(l.last_activity_at)}</span>
        </div>
        <div className="wc-r2">
          {p.icon && <MediaIcon kind={p.icon} />}
          <span className="wc-prev">{p.text}</span>
          {/* 人工: a colleague holds it, or 小爱 never answers this person
              (a dealer, their customer, progress 代理), switch or no switch —
              beside 交回小爱 too, until it is pressed (prototype ChatRow). */}
          {(l.ai_off || (!l.bot_enabled && !handed)) && (
            <span className="wc-human" title={l.ai_off || "同事在回，小爱不回。到「人工回复」可以交回小爱"}>
              <ShellIcon name="headset" size={12} />
              人工
            </span>
          )}
          {/* One thing to do per row, in the customer table's words, filled
              in the kit's job colours (急 red, a job amber, 售后 green). */}
          {job && (
            <span className={`lz-chip wc-job ${JOB_CHIP[job.tone]}`} title={job.title}>
              {job.label}
            </span>
          )}
          {handing && !l.ai_off && (!l.bot_enabled || handed) && <HandBackButton state={handed} onPress={() => onHandBack(l)} />}
        </div>
        {(tags.length > 0 || past) && (
          <div className="wc-r3">
            {tags.map((t) => (
              <LabelChip key={t.key} l={t} title={again} />
            ))}
            {past && <LabelChip l={past.label} title={past.title} past />}
          </div>
        )}
      </div>
    </div>
  );
});

/**
 * 交回小爱 on a row: the header's 小爱回复 without opening the chat. Green, as
 * that switch is when 小爱 answers; a tick once she does.
 */
function HandBackButton({ state, onPress }: { state?: "going" | "done" | "failed"; onPress: () => void }) {
  return (
    <button
      type="button"
      className={`wc-hb-one${state ? ` ${state}` : ""}`}
      disabled={state === "going" || state === "done"}
      title={state === "failed" ? "没交回，再按一次" : "交回小爱：客户再发消息由小爱回"}
      // The row opens the chat; this button only hands it back.
      onClick={(e) => {
        e.stopPropagation();
        onPress();
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {state === "done" ? (
        <>
          <ShellIcon name="check" size={13} />
          已交回
        </>
      ) : state === "going" ? (
        <>
          <i className="wc-hb-spin" aria-hidden="true" /> 交回中
        </>
      ) : state === "failed" ? (
        "没交回，再按"
      ) : (
        <>
          <ShellIcon name="sparkles" size={13} />
          交回小爱
        </>
      )}
    </button>
  );
}

/**
 * A chat on its way: the customer's name and face from the list straight away,
 * and grey bubbles where the messages will be, instead of the chat that was
 * open before staying put until the answer lands.
 */
function ChatLoading({ row, waID, onBack }: { row?: LeadRow; waID: string; onBack: () => void }) {
  const name = row?.name || (row && isMessenger(row) ? "Messenger 客户" : phoneLabel(waID));
  return (
    <div className="wc-main" aria-busy="true">
      <div className="wc-head">
        <button type="button" className="wc-ico wc-back" onClick={onBack} title="回到列表">
          <ShellIcon name="left" size={22} />
        </button>
        <span className="wc-av" style={avatarStyle(waID)} aria-hidden="true">
          <AvatarPic url={row?.avatar_url} />
          {initialOf(row?.name || waID)}
          <NetDot fb={waID.startsWith("fb:")} />
        </span>
        <div className="wc-head-t">
          <b className="wc-head-n">
            <span className="wc-name-t">{name}</span>
          </b>
          {/* The number's line, in the pages' placeholder look (PageSkel). */}
          <span className="wc-head-wait" aria-hidden="true">
            <i className="pskel-b" />
          </span>
        </div>
      </div>
      <div className="wc-body wc-skel" aria-label="读取中">
        {[62, 38, 70, 46, 30, 58].map((w, i) => (
          <i key={i} className={i % 3 === 1 ? "out" : ""} style={{ width: `${w}%` }} />
        ))}
      </div>
      <div className="wc-foot" />
    </div>
  );
}

/** How many 「先回这几位」 names: the next few to answer, not a second list. */
const FIRST_N = 3;
/** How many waiting chats are read to pick them from: the longest waits are not always the newest rows. */
const WAITING_MAX = 300;
/** 全部交回小爱's faces: the first so many, then how many more. */
const BULK_FACES = 15;
/** Waited this long, a customer is late: red, as on 今天 and 今天要做. */
const LATE_MS = 60 * 60e3;

/**
 * No chat open, on a computer: the right side was 75% of the screen and empty (owner 10/24, batch 2). It names
 * the customers to answer first — 要真人 first, then whoever has waited longest — one press each. It opens
 * nothing by itself: which chat a colleague takes is theirs to choose.
 */
function FirstReplies({ rows, onOpen }: { rows: LeadRow[]; onOpen: (waID: string) => void }) {
  // 「等了 7 小时」 moves on with the clock.
  useClock();
  const since = (r: LeadRow) => {
    const t = Date.parse(r.last_in_at ?? r.last_activity_at ?? "");
    return Number.isNaN(t) ? Date.now() : t;
  };
  // 回复客户's rule (customers.ts STEPS): written and not answered, or 小爱 asked for a person; our own numbers are on no list.
  const first = rows
    .filter((r) => !r.internal && (r.unanswered || r.needs_human))
    .sort((a, b) => Number(!!b.needs_human) - Number(!!a.needs_human) || since(a) - since(b))
    .slice(0, FIRST_N);
  if (first.length === 0)
    return (
      <div className="wc-blank">
        <div className="wc-first calm">
          <span className="wc-first-ok" aria-hidden="true">
            <ShellIcon name="check" size={18} />
          </span>
          <b>没有客户在等回复</b>
        </div>
      </div>
    );
  return (
    <div className="wc-blank">
      <div className="wc-first">
        <b className="wc-first-h">{first.length > 1 ? "先回这几位" : "先回这位"}</b>
        {first.map((r) => {
          const name = r.name || (isMessenger(r) ? "Messenger 客户" : phoneLabel(r.wa_id));
          const late = !!r.needs_human || Date.now() - since(r) >= LATE_MS;
          return (
            <button key={r.wa_id} type="button" className={`wc-first-r${late ? " late" : ""}`} onClick={() => onOpen(r.wa_id)} title={`打开和 ${name} 的对话`}>
              <span className="wc-av" style={avatarStyle(r.wa_id)} aria-hidden="true">
                <AvatarPic url={r.avatar_url} />
                {initialOf(r.name || r.wa_id)}
                <NetDot fb={isMessenger(r)} />
              </span>
              <span className="wc-first-t">
                <b>{name}</b>
                <span> · {r.unanswered ? `等了 ${sinceWord(r.last_in_at ?? r.last_activity_at)}` : "小爱请同事接手"}</span>
              </span>
              {r.needs_human && r.unanswered && <span className="lz-chip wc-job urgent">要真人</span>}
              <ShellIcon name="right" size={16} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Memo'd: App re-renders on every tab click and every count, and this view
// stays mounted while hidden — it should not redraw for a click elsewhere.
export default memo(LeadsView);
